import { AliasDefinition, SocialShareParams } from './types';
// Embedded social sharing logic (from socialShareUtils.ts)
export const DEFINITION_ALIAS: { [key: string]: AliasDefinition } = {
  text: {
    alias: ['text', 'post', 'content', 'tweet', 'thread', 'body'],
    description: 'The main text.',
  },
  url: {
    alias: ['url', 'u', 'link', 'site', 'domain', 'intentUrl', 'link_address', 'address', 'canonical', 'path'],
    description: 'A URL to include in the post.',
  },
  via: {
    alias: ['via', 'username', 'user', '@', 'uploader', 'author'],
    description: 'A username to attribute the post to.',
  },
  hashtags: {
    alias: ['hashtags', 'hashtag', 'tag', 'tags'],
    description: 'Comma-separated hashtags (without the # symbol).',
  },
  related: {
    alias: ['related', 'contributor', 'credit', 'cc', 'bcc'],
    description: 'Comma-separated related accounts.',
  },
  subject: {
    alias: ['title', 'subject', 'heading', 'header'],
    description: 'The email subject.',
  },
  body: {
    alias: ['text', 'post', 'content', 'tweet', 'thread', 'body'],
    description: 'The email body.',
  },
  cc: {
    alias: ['related', 'contributor', 'credit', 'cc', 'bcc'],
    description: 'Additional email addresses.',
  },
  bcc: {
    alias: ['related', 'contributor', 'credit', 'cc', 'bcc'],
    description: 'Additional email addresses.',
  },
  media: {
    alias: ['media', 'image', 'img', 'picture', 'photo', 'thumbnail', 'og:image'],
    description: 'An image URL to attach to the share.',
  },
  caption: {
    alias: ['description', 'desc', 'summary', 'caption', 'excerpt'],
    description: 'A short description/summary for the share.',
  },
};


export const SOCIAL_SHARE_PARAMS: { [platform: string]: SocialShareParams } = {
  x: {
    url: 'https://twitter.com/intent/tweet',
    params: {
      text: DEFINITION_ALIAS.text,
      url: DEFINITION_ALIAS.url,
      via: DEFINITION_ALIAS.via,
      hashtags: DEFINITION_ALIAS.hashtags,
      related: DEFINITION_ALIAS.related,
    },
    characters: {
      limit: 280,
      optimal: 100,
      mobile_cutoff: 150,
      url_len: 30,
    },
    alias: ['x', 'twitter', 'x.com', 'tweet', 'twitter.com'],
    hash_symbol: false,
  },
  facebook: {
    url: 'http://facebook.com/sharer.php',
    params: {
      u: DEFINITION_ALIAS.url,
    },
    characters: {
      limit: 63206,
      optimal: 50,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['facebook', 'fb', 'facebook.com', 'meta', 'meta.com'],
    hash_symbol: true,
  },
  threads: {
    url: 'https://www.threads.net/intent/post',
    params: {
      text: DEFINITION_ALIAS.text,
    },
    characters: {
      limit: 500,
      optimal: 150,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['threads', '@', 'threads.com', '@.com'],
    hash_symbol: true,
  },
  mailto: {
    url: 'mailto:',
    params: {
      subject: DEFINITION_ALIAS.subject,
      body: DEFINITION_ALIAS.body,
      cc: DEFINITION_ALIAS.cc,
      bcc: DEFINITION_ALIAS.bcc,
    },
    characters: {
      limit: null,
      optimal: null,
      mobile_cutoff: null,
      url_len: null,
    },
    alias: ['mailto', 'mail', 'email', 'email.com', 'mail.com'],
    hash_symbol: true,
  },
  minds: {
    url: 'https://www.minds.com/newsfeed/subscriptions/latest',
    params: {
      intentUrl: DEFINITION_ALIAS.url,
    },
    characters: {
      limit: 500,
      optimal: 125,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['minds', 'mindscollective', 'collective'],
    hash_symbol: true,
  },
  linkedin: {
    url: 'https://www.linkedin.com/sharing/share-offsite/',
    params: {
      url: DEFINITION_ALIAS.url,
    },
    characters: {
      limit: 3000,
      optimal: 150,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['linkedin', 'linked-in', 'linkedin.com', 'in'],
    hash_symbol: true,
  },
  reddit: {
    url: 'https://www.reddit.com/submit',
    params: {
      url: DEFINITION_ALIAS.url,
      title: DEFINITION_ALIAS.subject,
    },
    characters: {
      limit: 300,
      optimal: 100,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['reddit', 'reddit.com', 'r'],
    hash_symbol: false,
  },
  pinterest: {
    url: 'https://pinterest.com/pin/create/button/',
    params: {
      url: DEFINITION_ALIAS.url,
      media: DEFINITION_ALIAS.media,
      description: DEFINITION_ALIAS.caption,
    },
    characters: {
      limit: 500,
      optimal: 200,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['pinterest', 'pin', 'pinterest.com'],
    hash_symbol: true,
  },
  whatsapp: {
    url: 'https://api.whatsapp.com/send',
    params: {
      text: DEFINITION_ALIAS.text,
    },
    characters: {
      limit: 65536,
      optimal: 200,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['whatsapp', 'wa', 'whatsapp.com'],
    hash_symbol: true,
  },
  telegram: {
    url: 'https://t.me/share/url',
    params: {
      url: DEFINITION_ALIAS.url,
      text: DEFINITION_ALIAS.text,
    },
    characters: {
      limit: 4096,
      optimal: 200,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['telegram', 'tg', 't.me'],
    hash_symbol: true,
  },
  tumblr: {
    url: 'https://www.tumblr.com/widgets/share/tool',
    params: {
      canonicalUrl: DEFINITION_ALIAS.url,
      title: DEFINITION_ALIAS.subject,
      caption: DEFINITION_ALIAS.caption,
      tags: DEFINITION_ALIAS.hashtags,
    },
    characters: {
      limit: null,
      optimal: 250,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['tumblr', 'tumblr.com'],
    hash_symbol: true,
  },
  bluesky: {
    url: 'https://bsky.app/intent/compose',
    params: {
      text: DEFINITION_ALIAS.text,
    },
    characters: {
      limit: 300,
      optimal: 200,
      mobile_cutoff: 150,
      url_len: null,
    },
    alias: ['bluesky', 'bsky', 'bsky.app'],
    hash_symbol: true,
  },
};

export const META_VARS: { [key: string]: { min: [number, number]; max: [number, number] } } = {
  title: { min: [20, 30], max: [50, 60] },
  description: { min: [70, 100], max: [150, 160] },
  alt: { min: [10, 20], max: [100, 125] },
  context: { min: [20, 30], max: [70, 100] },
  keywords: { min: [10, 20], max: [200, 250] },
};